import { withMainControls } from '@eb/eds-structure';
import { trackEventFromState } from '@eb/site-analytics';
import flowRight from 'lodash/flowRight';
import { connect } from 'react-redux';
import { planRedirect } from '../actions/addons';
import {
    contactUsRedirect,
    goToDowngrade,
    goToUpdateVersionTwo,
    onChangePackageAfterConflicts,
    onKeepPackage,
    onKeepPackageAfterConflicts,
    setPackageOne,
    setPackageOneVersionTwo,
    setPackageTwo,
} from '../actions/changePlan';
import { checkConflicts, fetchConflicts } from '../actions/downgradeConflicts';
import {
    setSignupPackageOne,
    setSignupPackageThree,
    setSignupPackageTwo,
    setSignupPackageTwoAddon,
} from '../actions/onboarding';
import {
    redirectToChangePlan,
    redirectToContactSales,
    redirectToCreate,
    redirectToEssentials,
    redirectToPremium,
    redirectToProfessional,
    redirectToSignIn,
} from '../actions/redirect';
import {
    clearSelectedPackage,
    goToOverviewPage,
    goToPackageComparison,
    goToPackageOne,
    goToPackageThree,
    goToPackageTwo,
    goToPricing,
} from '../actions/routing';
import {
    AFTER_DOWNGRADE as AFTER_DOWNGRADE_TITLE,
    CURRENT_PACKAGE as BEFORE_DOWNGRADE_TITLE,
    defaultDowngradePackage,
    DOWNGRADE_CTA,
    getFeatures,
    getHeading,
    KEEP_PROFESSIONAL_CTA,
    PACKAGE_DOWNGRADE_BREADCRUMB,
    TABLE_TITLE,
    TABLE_TITLE_SHORT,
} from '../constants/downgrade';
import { PACKAGE_ONE_NAME, PACKAGE_TWO_NAME } from '../constants/main';
import {
    AFTER_DOWNGRADE as AFTER_UPDATE_SUBTITLE,
    AFTER_UPDATE,
    CURRENT_PACKAGE as BEFORE_UPDATE_TITLE,
    KEEP_CURRENT,
    PACKAGE_UPDATE_BREADCRUMB,
    PACKAGE_UPDATE_FEATURES,
    PACKAGE_UPDATE_HEADING,
    UPDATE_CTA,
} from '../constants/update';
import { isEssentialsNewVersion } from './utils';

// Redux Connect Utils
const _mapStateToProps = ({
    assortments: {
        assortmentsAvailability,
        assortmentsPricing,
        assortmentsTax,
        hasAssortmentsTax,
        hasFixedFee,
        feeBreakdown,
        packageTitles,
        selectedPackage,
    },
    env: { currencyFormat, defaultCurrency, domain, locale, tld },
    featureFlags: {
        crazyEggFeatureFlag,
        cappedFeeExperimentFeatureFlag,
        isPackageChangeDowngradeEnabled,
        useRefactoredPricingTableComparison,
    },
    user: {
        activeScopeUserId,
        isAuthenticated,
        userId,
        userPlan,
        userType,
        activeOrganization,
    },
    eid,
    gaSettings,
    addons: { subscriptionEndDate },
    showLowAtvUi,
    currencySymbol,
    localeDefaultPlan,
}) => ({
    activeScopeUserId,
    assortmentsAvailability,
    assortmentsPricing,
    assortmentsTax,
    hasAssortmentsTax,
    feeBreakdown,
    hasFixedFee,
    packageTitles,
    selectedPackage,
    crazyEggFeatureFlag,
    cappedFeeExperimentFeatureFlag,
    domain,
    locale,
    tld,
    isAuthenticated,
    trackEvent: (action) =>
        trackEventFromState({ gaSettings, eventId: eid }, action),
    userId,
    userType,
    userPlan,
    activeOrganization,
    subscriptionEndDate,
    showLowAtvUi,
    currencySymbol,
    localeDefaultPlan,
    defaultCurrency,
    currencyFormat,
    isPackageChangeDowngradeEnabled,
    useRefactoredPricingTableComparison,
});

const _mapDispatchToProps = {
    clearSelectedPackage,
    contactUsRedirect,
    goToDowngrade,
    goToPricing,
    goToPackageComparison,
    goToOverviewPage,
    goToPackageOne,
    goToPackageTwo,
    goToPackageThree,
    goToUpdateVersionTwo,
    planRedirect,
    redirectToContactSales,
    redirectToSignIn,
    redirectToCreate,
    redirectToChangePlan,
    redirectToEssentials,
    redirectToProfessional,
    redirectToPremium,
    setSignupPackageOne,
    setSignupPackageTwo,
    setSignupPackageTwoAddon,
    setSignupPackageThree,
    setPackageOne,
    setPackageOneVersionTwo,
    setPackageTwo,
};

const _updateMapStateToProps = ({
    assortments: { assortmentsAvailability },
    eid,
    env: { locale },
    featureFlags: { isPackageChangeDowngradeEnabled },
    gaSettings,
    user: { userId, userPlan },
}) => ({
    assortmentsAvailability,
    breadcrumb: PACKAGE_UPDATE_BREADCRUMB,
    changePackageCTA: UPDATE_CTA,
    currentPackageTitle: BEFORE_UPDATE_TITLE,
    features: PACKAGE_UPDATE_FEATURES,
    firstHeading: PACKAGE_UPDATE_HEADING,
    isPackageChangeDowngradeEnabled,
    keepPackageCTA: KEEP_CURRENT,
    locale,
    newPackageSubtitle: AFTER_UPDATE_SUBTITLE,
    newPackageTitle: AFTER_UPDATE,
    trackEvent: (action) =>
        trackEventFromState({ gaSettings, eventId: eid }, action),
    userId,
    userPlan,
});

const _updateMapDispatchToProps = {
    onChangePackage: setPackageOneVersionTwo,
    onKeepPackage,
};

const _downgradeMapStateToProps = ({
    assortments: { assortmentsAvailability },
    featureFlags: { isPackageChangeDowngradeEnabled },
    env: { locale },
    eid,
    gaSettings,
    user: { userId, userPlan },
}) => {
    const downgrade =
        assortmentsAvailability === null
            ? defaultDowngradePackage
            : assortmentsAvailability.downgrade;

    // Experiment in Australia is using a new version of essentials
    const useNewVersion = isEssentialsNewVersion(locale, downgrade);
    const firstHeading = getHeading(
        useNewVersion,
        isPackageChangeDowngradeEnabled,
    );

    return {
        assortmentsAvailability,
        breadcrumb: PACKAGE_DOWNGRADE_BREADCRUMB,
        changePackageCTA: DOWNGRADE_CTA,
        currentPackageSubtitle: BEFORE_DOWNGRADE_TITLE,
        currentPackageTitle: PACKAGE_TWO_NAME,
        features: getFeatures(useNewVersion),
        firstHeading,
        isPackageChangeDowngradeEnabled,
        keepPackageCTA: KEEP_PROFESSIONAL_CTA,
        locale,
        newPackageSubtitle: AFTER_DOWNGRADE_TITLE,
        newPackageTitle: PACKAGE_ONE_NAME,
        tableTitle: TABLE_TITLE,
        tableTitleShort: TABLE_TITLE_SHORT,
        trackEvent: (action) =>
            trackEventFromState({ gaSettings, eventId: eid }, action),
        userId,
        userPlan,
    };
};

const _downgradeMapDispatchToProps = {
    onChangePackage: checkConflicts,
    onKeepPackage,
};

export const pageConnector = (ComponentClass, additionalProps = {}) =>
    connect(_mapStateToProps, { ..._mapDispatchToProps, ...additionalProps })(
        ComponentClass,
    );

export const downgradePageConnector = (ComponentClass) =>
    connect(
        _downgradeMapStateToProps,
        _downgradeMapDispatchToProps,
    )(ComponentClass);

export const updatePageConnector = (ComponentClass) =>
    connect(_updateMapStateToProps, _updateMapDispatchToProps)(ComponentClass);

export const pageConnectorWithMainControls = (ComponentClass) =>
    flowRight(
        connect(_mapStateToProps, _mapDispatchToProps),
        withMainControls,
    )(ComponentClass);

export const downgradeConflictPageConnector = (ComponentClass) =>
    flowRight(
        connect(
            ({ conflicts, fetchingConflicts, ...state }) => ({
                ..._downgradeMapStateToProps(state),
                conflicts,
                isConflictsLoading: fetchingConflicts,
            }),
            {
                onPageChange: fetchConflicts,
                onRedirectToDowngrade: goToDowngrade,
                onChangePackage: onChangePackageAfterConflicts,
                onKeepPackage: onKeepPackageAfterConflicts,
            },
        ),
        withMainControls,
    )(ComponentClass);
